import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowLeft, ArrowRight, Check, CheckCircle2, Link2 } from 'lucide-react';
import { diffChaines } from './EtapeRapport';

/**
 * Modal d'arbitrage « un cas par un » — v2, gabarit maquette utilisateur.
 *
 * Tout est CONCRET : le titre énonce le fait exact (« Le nom de Léa a changé
 * d'une lettre »), le diff du nom est rendu au caractère près, « Ce qui
 * concorde » liste les vraies valeurs, et les réponses sont des verbes
 * d'action avec leur conséquence en une ligne. « Passer pour l'instant » est
 * un lien discret. Les réponses écrivent dans les mêmes résolutions que
 * l'inline (source unique) ; fermer ne perd rien.
 */

// Rendu lettre à lettre : partie retirée barrée rouge sur l'ancien nom,
// partie ajoutée surlignée verte sur le nouveau. Ancien = TanoEd, nouveau = fichier.
function DiffTexte({ avant, apres }) {
    const d = diffChaines(avant, apres);
    return (
        <div className="flex items-center gap-3 flex-wrap text-2xl tracking-wide">
            <span className="text-slate-400">
                {d.prefixe}
                {d.retire ? <span className="line-through decoration-red-400 text-red-400">{d.retire}</span> : null}
                {d.suffixe}
            </span>
            <ArrowRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
            <span className="text-slate-800 font-medium">
                {d.prefixe}
                {d.ajoute ? <span className="bg-emerald-100 text-emerald-700 rounded px-0.5">{d.ajoute}</span> : null}
                {d.suffixe}
            </span>
        </div>
    );
}

function ModalArbitrage({ cas, sorties, onFermer }) {
    // Index du cas affiché ; length = écran final de récap.
    const [index, setIndex] = useState(() => {
        const premier = cas.findIndex((c) => !c.repondu);
        return premier === -1 ? cas.length : premier;
    });
    const conteneurRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onFermer(); };
        window.addEventListener('keydown', onKey);
        if (conteneurRef.current) conteneurRef.current.focus();
        return () => {
            window.removeEventListener('keydown', onKey);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [onFermer]);

    const avancer = () => setIndex((i) => Math.min(i + 1, cas.length));
    const repondre = (c, valeur) => {
        c.repondre(valeur);
        // Court feedback visuel (la réponse se surligne), puis cas suivant.
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(avancer, 350);
    };

    const nbRepondus = cas.filter((c) => c.repondu).length;
    const fini = index >= cas.length;
    const c = fini ? null : cas[index];

    return (
        <div
            className="fixed inset-0 z-[10010] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
            onClick={onFermer}
        >
            <div
                ref={conteneurRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label="Arbitrage des cas particuliers"
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                {/* En-tête : type · fil (gauche), pastille sujet (droite) */}
                <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-3.5 flex items-center gap-3 rounded-t-2xl">
                    <p className="text-sm text-slate-500">
                        {fini ? 'Récapitulatif' : (
                            <>
                                <span className="font-medium text-slate-600">{c.typeLabel}</span>
                                {' · '}{index + 1} sur {cas.length}
                            </>
                        )}
                    </p>
                    <div className="flex items-center gap-1">
                        {cas.map((x, i) => (
                            <button
                                key={x.id}
                                onClick={() => setIndex(i)}
                                title={x.sujet}
                                aria-label={`Cas ${i + 1} : ${x.sujet}`}
                                className={`w-2 h-2 rounded-full transition-colors ${
                                    x.repondu ? 'bg-emerald-500' : i === index ? 'bg-blue-600' : 'bg-slate-300'
                                }`}
                            />
                        ))}
                    </div>
                    {!fini ? (
                        <span className="ml-auto text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 whitespace-nowrap">
                            {c.sujetPill}
                            {c.entLie ? (
                                <Link2 className="w-3 h-3 text-red-600 inline ml-1.5 -mt-0.5" />
                            ) : null}
                        </span>
                    ) : null}
                    <button
                        onClick={onFermer}
                        aria-label="Fermer"
                        className={`${fini ? 'ml-auto' : ''} p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {fini ? (
                    /* Écran final : récap + retour au rapport */
                    <div className="p-6 text-center">
                        <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-emerald-500 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-700 mb-1">
                            {nbRepondus} réponse{nbRepondus > 1 ? 's' : ''} sur {cas.length} cas
                        </h3>
                        <p className="text-sm text-slate-500 mb-1">
                            {nbRepondus < cas.length
                                ? `${cas.length - nbRepondus} cas rest${cas.length - nbRepondus > 1 ? 'ent' : 'e'} sans réponse : il faudra y répondre avant de valider.`
                                : 'Tous les cas sont tranchés.'}
                        </p>
                        {sorties > 0 ? (
                            <p className="text-sm text-slate-500 mb-4">
                                Il restera {sorties} sortie{sorties > 1 ? 's' : ''} à confirmer, puis la mise à jour de la vérification.
                            </p>
                        ) : <span className="block mb-4" />}
                        <button
                            onClick={onFermer}
                            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30"
                        >
                            Revenir au rapport
                        </button>
                    </div>
                ) : (
                    <div className="px-6 py-5">
                        {/* Le fait + l'inférence */}
                        <h3 className="text-xl font-bold text-slate-800 mb-1">{c.titre}</h3>
                        {c.sousTitre ? <p className="text-sm text-slate-500 mb-4">{c.sousTitre}</p> : <div className="mb-4" />}

                        {/* Diff du nom, lettre à lettre */}
                        {c.diffNom ? (
                            <div className="mb-4">
                                <DiffTexte avant={c.diffNom.avant} apres={c.diffNom.apres} />
                            </div>
                        ) : null}

                        {/* Ce qui concorde / diffère : les vraies valeurs */}
                        {c.concordances && c.concordances.length > 0 ? (
                            <div className="border-t border-slate-100 pt-3 mb-4">
                                {c.concordances.some((k) => k.etat === 'ok') ? (
                                    <>
                                        <p className="text-xs font-medium text-slate-400 mb-1.5">Ce qui concorde</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mb-2">
                                            {c.concordances.filter((k) => k.etat === 'ok').map((k) => (
                                                <p key={k.label} className="text-sm text-slate-700 flex items-center gap-1.5">
                                                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                                    <span>{k.label}&nbsp;: <span className="font-medium">{k.valeur}</span></span>
                                                </p>
                                            ))}
                                        </div>
                                    </>
                                ) : null}
                                {c.concordances.some((k) => k.etat === 'diff') ? (
                                    <>
                                        <p className="text-xs font-medium text-slate-400 mb-1.5">Ce qui diffère</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                                            {c.concordances.filter((k) => k.etat === 'diff').map((k) => (
                                                <p key={k.label} className="text-sm text-slate-700 flex items-center gap-1.5">
                                                    <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                                                    <span>{k.label}&nbsp;: <span className="font-medium">{k.valeur}</span></span>
                                                </p>
                                            ))}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        ) : (
                            <p className="border-t border-slate-100 pt-3 mb-4 text-xs text-slate-400">{c.faits}</p>
                        )}

                        {/* Les réponses : chaque carte est une HISTOIRE — le prof
                            choisit celle qui correspond à la réalité, les effets
                            en puces disent ce qui en découle */}
                        <p className="text-sm text-slate-500 mb-2">Que s'est-il passé entre les deux imports ?</p>
                        <div
                            role="radiogroup"
                            aria-label="Que s'est-il passé entre les deux imports ?"
                            className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4 items-stretch"
                        >
                            {c.options.filter((o) => o.value !== '').map((o) => {
                                const actif = c.valeur === o.value;
                                if (o.desactive) {
                                    return (
                                        <div
                                            key={o.value}
                                            role="radio"
                                            aria-checked={false}
                                            aria-disabled="true"
                                            className="border border-slate-200 bg-slate-50 rounded-xl px-4 py-3.5 opacity-70"
                                        >
                                            <p className="text-[15px] font-semibold text-slate-400">{o.titreRecit}</p>
                                            <p className="text-xs text-slate-500 mt-1">{o.raison}</p>
                                        </div>
                                    );
                                }
                                return (
                                    <button
                                        key={o.value}
                                        role="radio"
                                        aria-checked={actif}
                                        onClick={() => repondre(c, o.value)}
                                        className={`text-left border rounded-xl px-4 py-3.5 transition-all ${
                                            actif
                                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                                : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'
                                        }`}
                                    >
                                        {o.badge ? (
                                            <span className={`inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full mb-1.5 ${
                                                o.badge === 'probable'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-amber-100 text-amber-800'
                                            }`}>
                                                {o.badge === 'probable' ? 'Le plus probable' : 'Rare'}
                                            </span>
                                        ) : null}
                                        <p className="text-[15px] font-semibold text-slate-800">{o.titreRecit}</p>
                                        {o.sousRecit ? (
                                            <p className="text-sm text-slate-500 mt-0.5">{o.sousRecit}</p>
                                        ) : null}
                                        {o.effets && o.effets.length > 0 ? (
                                            <ul className="mt-2.5 pt-2.5 border-t border-slate-100 space-y-1.5">
                                                {o.effets.map((ef) => (
                                                    <li key={ef} className="text-[13px] text-slate-600 flex items-start gap-2">
                                                        <span className="w-1.5 h-1.5 rounded-[2px] border border-slate-300 mt-[6px] flex-shrink-0" />
                                                        {ef}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                        {o.note ? (
                                            <p className="text-xs text-slate-400 mt-2">{o.note}</p>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Pied : précédent · passer (lien) · suivant si déjà répondu */}
                        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                            <button
                                onClick={() => setIndex((i) => Math.max(i - 1, 0))}
                                disabled={index === 0}
                                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-3 py-2 rounded-xl transition-colors disabled:text-slate-300 disabled:hover:bg-transparent"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Précédent
                            </button>
                            <div className="flex items-center gap-4">
                                {/* Une décision se tranche : le lien n'existe que là où
                                    « plus tard » EST une réponse (conflits : la fiche est
                                    protégée, la question revient au prochain import). */}
                                {c.options.some((o) => o.value === '') ? (
                                    <button
                                        onClick={() => { repondre(c, ''); }}
                                        className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
                                    >
                                        Passer pour l'instant
                                    </button>
                                ) : null}
                                {c.repondu ? (
                                    <button
                                        onClick={avancer}
                                        className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded-xl transition-colors"
                                    >
                                        {index === cas.length - 1 ? 'Terminer' : 'Suivant'}
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ModalArbitrage;
